/**
 * Cadastro do grupo de veículos: nome, descrição e as PLACAS que fazem parte
 * (escolhidas da frota própria ativa — o modelo do caminhão não é critério), e
 * o plano preventivo do grupo, opcional.
 *
 * Salvar aplica na hora ao Plano Preventivo: placa do grupo com plano segue o
 * plano do grupo (vence o ERP; ajuste manual na placa continua valendo); placa
 * que sai volta ao ERP e ao padrão. Uma placa está em um grupo só.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MaintenanceGroupFleetDto, MaintenanceGroupPlanItemDto, MaintenanceKnownComponentDto, MaintenanceVehicleGroupDetailDto,
} from "@bi/types";
import { api, ApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { useCapabilities } from "../features/maintenance/useCapabilities";

interface Form {
  name: string;
  description: string;
  active: boolean;
  plates: string[];
  planItems: Array<{ componentCode: string | null; label: string; category: string; intervalKm: number | null; intervalDays: number | null }>;
}
const vazio: Form = { name: "", description: "", active: true, plates: [], planItems: [] };

export function MaintenanceVehicleGroupEditorPage() {
  const { id = "novo" } = useParams();
  const novo = id === "novo";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useCapabilities();
  const edita = can("workorder.manage");

  const grupo = useQuery({
    queryKey: ["maintenance", "vehicle-group", id],
    queryFn: () => api.get<MaintenanceVehicleGroupDetailDto>(`/maintenance/vehicle-groups/${id}`),
    enabled: !novo,
  });
  const componentes = useQuery({
    queryKey: ["maintenance", "vehicle-groups", "components"],
    queryFn: () => api.get<{ items: MaintenanceKnownComponentDto[] }>("/maintenance/vehicle-groups/components"),
    staleTime: Infinity,
  });

  const [f, setF] = useState<Form | null>(novo ? vazio : null);
  const [msg, setMsg] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const d = grupo.data;
    if (!d) return;
    setF({
      name: d.group.name, description: d.group.description ?? "", active: d.group.active,
      plates: d.vehicles.map((v) => v.plate),
      planItems: d.group.planItems.map((i: MaintenanceGroupPlanItemDto) => ({ ...i })),
    });
  }, [grupo.data]);

  const salvar = useMutation({
    mutationFn: () => (novo
      ? api.post<{ group: { id: string }; plates: number; applied: { created: number; updated: number; deactivated: number } }>("/maintenance/vehicle-groups", f)
      : api.put<{ group: { id: string }; plates: number; applied: { created: number; updated: number; deactivated: number } }>(`/maintenance/vehicle-groups/${id}`, f)),
    onSuccess: (r) => {
      setErro(null);
      setMsg(`Salvo: ${r.plates} placa(s) no grupo. Plano recalculado (${r.applied.created} criado(s), ${r.applied.updated} atualizado(s)${r.applied.deactivated ? `, ${r.applied.deactivated} desativado(s)` : ""}).`);
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      if (novo) navigate(`/frota/manutencao/grupos/${r.group.id}`, { replace: true });
    },
    onError: (e) => { setMsg(null); setErro(e instanceof ApiError ? e.message : "Não foi possível salvar."); },
  });

  if (!f) return <div className="p-8 text-sm text-gray-500">Carregando grupo…</div>;
  const set = (patch: Partial<Form>) => { setMsg(null); setF({ ...f, ...patch }); };
  const problemas = [
    f.name.trim().length < 2 ? "Dê um nome ao grupo." : null,
    f.plates.length === 0 ? "Escolha as placas do grupo." : null,
    ...f.planItems.map((i) => (!i.label.trim() ? "Há item do plano sem nome." : !i.intervalKm && !i.intervalDays ? `"${i.label}": informe KM ou dias.` : null)),
  ].filter((x): x is string => !!x);

  return (
    <div className="space-y-5 pb-24">
      <PageHeader
        title={novo ? "Novo grupo de veículos" : f.name}
        subtitle="Nome, descrição e as placas do grupo. O plano do grupo é opcional."
        actions={<Link className="btn-secondary" to="/frota/manutencao/grupos">Voltar</Link>}
      />

      <div className="card p-5 grid gap-4">
        <label className="block">
          <span className="text-sm text-gray-600">Nome do grupo</span>
          <input className="input w-full mt-1" value={f.name} disabled={!edita} placeholder="Ex.: Frota Transbordo" onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-sm text-gray-600">Descrição</span>
          <input className="input w-full mt-1" value={f.description} disabled={!edita} placeholder="Ex.: Veículos utilizados nas operações de transbordo." onChange={(e) => set({ description: e.target.value })} />
        </label>
        {!novo && (
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={f.active} disabled={!edita} onChange={(e) => set({ active: e.target.checked })} />
            Grupo ativo (desativado, as placas voltam ao ERP e ao plano padrão)
          </label>
        )}
      </div>

      <EscolhaDePlacas groupId={novo ? null : id} selecionadas={f.plates} edita={edita} onChange={(plates) => set({ plates })} />

      <section className="card p-5 space-y-3">
        <div>
          <h3 className="font-semibold text-gray-900">Plano de manutenção do grupo <span className="text-sm font-normal text-gray-400">(opcional)</span></h3>
          <p className="text-sm text-gray-500">
            Manutenções, revisões e intervalos. Com plano, ele vale para cada placa do grupo e vence o intervalo do ERP.
            Sem plano, o grupo só organiza a frota e as placas seguem o ERP e o plano padrão.
          </p>
        </div>
        {f.planItems.length > 0 && (
          <div className="divide-y divide-gray-100 border border-surface-border rounded-lg">
            {f.planItems.map((i, k) => (
              <div key={k} className="p-3 grid sm:grid-cols-12 gap-2 items-center">
                <input className="input sm:col-span-5" value={i.label} disabled={!edita} placeholder="Serviço (ex.: Troca de óleo do motor)" onChange={(e) => set({ planItems: f.planItems.map((x, j) => (j === k ? { ...x, label: e.target.value } : x)) })} />
                <input className="input sm:col-span-3" value={i.category} disabled={!edita} placeholder="Categoria" onChange={(e) => set({ planItems: f.planItems.map((x, j) => (j === k ? { ...x, category: e.target.value } : x)) })} />
                <input className="input sm:col-span-2" inputMode="numeric" disabled={!edita} placeholder="a cada KM" value={i.intervalKm ?? ""} onChange={(e) => set({ planItems: f.planItems.map((x, j) => (j === k ? { ...x, intervalKm: e.target.value ? Number(e.target.value.replace(/\D/g, "")) : null } : x)) })} />
                <input className="input sm:col-span-1" inputMode="numeric" disabled={!edita} placeholder="dias" value={i.intervalDays ?? ""} onChange={(e) => set({ planItems: f.planItems.map((x, j) => (j === k ? { ...x, intervalDays: e.target.value ? Number(e.target.value.replace(/\D/g, "")) : null } : x)) })} />
                {edita && <button className="btn-ghost text-xs text-danger-600 sm:col-span-1" onClick={() => set({ planItems: f.planItems.filter((_, j) => j !== k) })}>Remover</button>}
              </div>
            ))}
          </div>
        )}
        {edita && (
          <div className="flex flex-wrap gap-2">
            <select className="input w-80" value="" onChange={(e) => {
              const c = componentes.data?.items.find((x) => x.code === e.target.value);
              if (c && !f.planItems.some((x) => x.componentCode === c.code)) {
                set({ planItems: [...f.planItems, { componentCode: c.code, label: c.label, category: c.category, intervalKm: c.intervalKm, intervalDays: c.intervalDays }] });
              }
            }}>
              <option value="">+ Componente conhecido…</option>
              {(componentes.data?.items ?? []).filter((c) => !f.planItems.some((x) => x.componentCode === c.code)).map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            <button className="btn-secondary" onClick={() => set({ planItems: [...f.planItems, { componentCode: null, label: "", category: "", intervalKm: null, intervalDays: null }] })}>+ Item novo</button>
          </div>
        )}
      </section>

      {edita && (
        <div className="fixed bottom-0 left-0 lg:left-64 right-0 bg-white border-t border-surface-border p-3 z-30">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3">
            <div className="flex-1 text-sm">
              {erro ? <span className="text-danger-600">{erro}</span>
                : msg ? <span className="text-tm-700">{msg}</span>
                  : problemas.length ? <span className="text-warning-700">{problemas[0]}</span>
                    : <span className="text-gray-500">{f.plates.length} placa(s) · {f.planItems.length ? `${f.planItems.length} item(ns) no plano` : "sem plano próprio"}</span>}
            </div>
            <button className="btn-primary" disabled={problemas.length > 0 || salvar.isPending} onClick={() => salvar.mutate()}>
              {salvar.isPending ? "Salvando…" : novo ? "Criar grupo" : "Salvar grupo"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * As placas do grupo, escolhidas da frota própria ativa: busca, filtro por marca
 * e cavalo/carreta. A lista oferece só placas SEM grupo (mais as que já são deste
 * grupo, para poder tirá-las): placa de outro grupo não aparece.
 */
function EscolhaDePlacas({ groupId, selecionadas, edita, onChange }: {
  groupId: string | null;
  selecionadas: string[];
  edita: boolean;
  onChange: (plates: string[]) => void;
}) {
  const [busca, setBusca] = useState("");
  const [marca, setMarca] = useState("");
  const [tipo, setTipo] = useState<"" | "tractor" | "trailer">("");
  const [mostrar, setMostrar] = useState<"disponiveis" | "escolhidas">(selecionadas.length ? "escolhidas" : "disponiveis");

  const frota = useQuery({
    queryKey: ["maintenance", "vehicle-groups", "fleet"],
    queryFn: () => api.get<MaintenanceGroupFleetDto>("/maintenance/vehicle-groups/fleet"),
  });
  const marcadas = new Set(selecionadas);
  const todas = frota.data?.vehicles ?? [];
  const marcas = [...new Set(todas.map((v) => v.brand).filter((b): b is string => !!b))].sort();
  const termo = busca.trim().toUpperCase();
  const lista = todas.filter((v) =>
    (!v.group || v.group.id === groupId || marcadas.has(v.plate))
    && (mostrar !== "escolhidas" || marcadas.has(v.plate))
    && (!tipo || v.isTrailer === (tipo === "trailer"))
    && (!marca || v.brand === marca)
    && (!termo || `${v.plate} ${v.brand ?? ""} ${v.model ?? ""} ${v.year ?? ""}`.toUpperCase().includes(termo)),
  );
  const alternar = (plate: string) => {
    if (!edita) return;
    onChange(marcadas.has(plate) ? selecionadas.filter((p) => p !== plate) : [...selecionadas, plate]);
  };
  const todasListadas = lista.length > 0 && lista.every((v) => marcadas.has(v.plate));

  return (
    <section className="card p-5 space-y-3">
      <div>
        <h3 className="font-semibold text-gray-900">Veículos do grupo <span className="text-sm font-normal text-gray-500">— {selecionadas.length} placa(s)</span></h3>
        <p className="text-sm text-gray-500">Escolha as placas da frota própria ativa que ainda não estão em nenhum grupo. Uma placa está em um grupo só: para movê-la, tire-a do grupo atual primeiro.</p>
      </div>
      {selecionadas.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {[...selecionadas].sort().map((p) => (
            <span key={p} className="inline-flex items-center gap-1 rounded border border-surface-border bg-surface-muted px-2 py-0.5 font-mono text-xs">
              {p}
              {edita && <button className="text-gray-400 hover:text-danger-600" aria-label={`Tirar ${p}`} onClick={() => alternar(p)}>×</button>}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input className="input w-56" placeholder="Buscar placa, marca, modelo, ano" value={busca} onChange={(e) => setBusca(e.target.value)} />
        <select className="input w-44" value={marca} onChange={(e) => setMarca(e.target.value)}>
          <option value="">Toda marca</option>
          {marcas.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="input w-40" value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
          <option value="">Cavalos e carretas</option>
          <option value="tractor">Só cavalos</option>
          <option value="trailer">Só carretas</option>
        </select>
        <select className="input w-44" value={mostrar} onChange={(e) => setMostrar(e.target.value as typeof mostrar)}>
          <option value="disponiveis">Sem grupo</option>
          <option value="escolhidas">Só as escolhidas</option>
        </select>
      </div>
      <div className="max-h-96 overflow-y-auto border border-surface-border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-xs text-gray-500 sticky top-0">
            <tr>
              <th className="px-3 py-2 w-8">
                {edita && (
                  <input
                    type="checkbox"
                    aria-label="Marcar todas as listadas"
                    checked={todasListadas}
                    onChange={(e) => {
                      const listadas = lista.map((v) => v.plate);
                      onChange(e.target.checked ? [...new Set([...selecionadas, ...listadas])] : selecionadas.filter((p) => !listadas.includes(p)));
                    }}
                  />
                )}
              </th>
              <th className="text-left px-3 py-2">Placa</th>
              <th className="text-left px-3 py-2">Marca</th>
              <th className="text-left px-3 py-2">Modelo (ERP)</th>
              <th className="text-left px-3 py-2">Ano</th>
              <th className="text-left px-3 py-2">Grupo atual</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {frota.isLoading && <tr><td colSpan={6} className="px-3 py-4 text-gray-500">Carregando frota…</td></tr>}
            {lista.map((v) => {
              const deOutro = v.group && v.group.id !== groupId ? v.group.name : null;
              return (
                <tr key={v.plate} className={`${edita ? "cursor-pointer hover:bg-surface-muted" : ""} ${marcadas.has(v.plate) ? "bg-tm-50/50" : ""}`} onClick={() => alternar(v.plate)}>
                  <td className="px-3 py-1.5">
                    <input type="checkbox" disabled={!edita} checked={marcadas.has(v.plate)} onChange={() => alternar(v.plate)} onClick={(e) => e.stopPropagation()} />
                  </td>
                  <td className="px-3 py-1.5 font-mono">{v.plate}<span className="ml-1 text-xs text-gray-400">{v.isTrailer ? "carreta" : "cavalo"}</span></td>
                  <td className="px-3 py-1.5">{v.brand ?? "—"}</td>
                  <td className="px-3 py-1.5 text-gray-600">{v.model ?? "—"}</td>
                  <td className="px-3 py-1.5">{v.year ?? "—"}</td>
                  <td className="px-3 py-1.5 text-gray-600">
                    {deOutro ? <span className={marcadas.has(v.plate) ? "text-warning-700" : ""}>{deOutro}{marcadas.has(v.plate) ? " → muda para este" : ""}</span> : v.group ? "este grupo" : "—"}
                  </td>
                </tr>
              );
            })}
            {!frota.isLoading && lista.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-gray-500">Nenhuma placa com estes filtros.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400">{lista.length} placa(s) listada(s) · {selecionadas.length} escolhida(s)</p>
    </section>
  );
}
